/**
 * canvas-engine/renderer/elements/SocialElement.jsx
 *
 * OWN per-type element renderer for `social` elements (header/footer chrome).
 *
 * ZERO REUSE: imports NOTHING from template-builder/ or academy-ui/src/template/.
 *
 * Inner-content vs positioning CONTRACT: renders ONLY the inner social row,
 * filling its positioned box. The parent CanvasRenderer owns positioning. DOM
 * output is identical in builder and reader modes. (Req 4.2, 17.1)
 *
 * Renders `content.items` as social links/icons. Each item may carry an icon
 * asset (resolved via OWN `assetRef`), a label, and a url. Pure / presentational.
 */

import React from 'react';
import { assetRef } from '../../model/assetRef';

function str(v) {
  return v == null ? '' : String(v);
}

function color(v, fallback) {
  return typeof v === 'string' && v.length > 0 ? v : fallback;
}

function num(v, fallback) {
  return Number.isFinite(v) ? v : fallback;
}

/**
 * Render a row of social links/icons.
 *
 * @param {object} props
 * @param {object} props.element normalized `social` Canvas_Element
 */
export default function SocialElement({ element }) {
  const content = (element && element.content) || {};
  const style = (element && element.style) || {};

  const items = Array.isArray(content.items) ? content.items : [];
  const iconSize = num(style.iconSize, 20);

  const rootStyle = {
    width: '100%',
    height: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    gap: num(style.gap, 16),
    flexWrap: 'wrap',
    background: color(style.background, 'transparent'),
    color: color(style.color, '#0f172a'),
  };

  return (
    <div className="ce-social" style={rootStyle}>
      {items.map((item, index) => {
        const it = item && typeof item === 'object' ? item : {};
        const url = str(it.url) || str(it.href);
        const label = str(it.label) || str(it.network) || str(it.platform) || 'social link';
        const asset = assetRef(it.icon != null ? it.icon : it.asset);
        const iconSrc = asset.displayLink || asset.storedLink || '';
        const key = str(it.id) || `social-item-${index}`;

        return (
          <a
            key={key}
            className="ce-social__link"
            href={url || undefined}
            aria-label={label}
            style={{ display: 'inline-flex', alignItems: 'center', color: 'inherit', textDecoration: 'none' }}
          >
            {iconSrc ? (
              <img
                src={iconSrc}
                alt={label}
                style={{ width: iconSize, height: iconSize, objectFit: 'contain', display: 'block' }}
              />
            ) : (
              <span style={{ fontSize: num(style.fontSize, 14), fontWeight: 500 }}>{label}</span>
            )}
          </a>
        );
      })}
    </div>
  );
}
